/**
 * A project's stack: the infrastructure and deploy workflow CompBox writes
 * into the project's repository (as a pull request) so the project can
 * deploy to the organization's cloud.
 *
 * - infra/ — Terraform for one Cloud Run service per environment, with its
 *   own runtime account, state kept in the organization's state bucket.
 * - .github/workflows/compbox.yml — on every push to the default branch,
 *   deploys to dev; stage and prod are deployed on request ("Run workflow"),
 *   and prod waits for the approval set on its GitHub environment.
 * - .github/workflows/compbox-deploy.yml — the deploy itself: sign in to
 *   Google Cloud through Workload Identity (no stored keys), apply the
 *   environment's Terraform, keep DATABASE_URL (if any) in Secret Manager,
 *   build the Dockerfile, run the app's migrate script in an approvable
 *   "<env>-migrations" job, then deploy and report the URL.
 * - .github/workflows/compbox-teardown.yml (full tier only) — destroys stage
 *   or prod on request, after the caller types "<app>-<environment>".
 * - infra/database.tf — only with database "cloudsql": a Cloud SQL for
 *   PostgreSQL database per environment, whose DATABASE_URL Terraform keeps
 *   in Secret Manager. It is created by the first deploy after the merge.
 */
import { gcpStateBucket } from "./cloud-setup.js";
import { buildAwsStackFiles } from "./stack-files-aws.js";
export const STACK_TIERS = {
    dev: { label: "Dev only", environments: ["dev"] },
    full: { label: "Dev, stage and prod", environments: ["dev", "stage", "prod"] },
};
export function stackEnvironments(tier) {
    return STACK_TIERS[tier].environments;
}
/** The workflow CompBox starts to deploy, and the run title it gives each deploy. */
export const STACK_WORKFLOW = "compbox.yml";
export function deployRunTitle(environment) {
    return `Deploy to ${environment}`;
}
/** The workflow that tears down stage or prod (full tier only), and its run title. */
export const TEARDOWN_WORKFLOW = "compbox-teardown.yml";
export const TEARDOWN_ENVIRONMENTS = ["stage", "prod"];
export function teardownRunTitle(environment) {
    return `Tear down ${environment}`;
}
/** What the caller has to type to confirm a teardown. */
export function teardownConfirmation(app, environment) {
    return `${app}-${environment}`;
}
/** Cloud Run's address for a service: https://<service>-<project number>.<region>.run.app */
export function cloudRunUrl(service, projectNumber, region) {
    return `https://${service}-${projectNumber}.${region}.run.app`;
}
/**
 * A short, safe service name from a repository name: lowercase letters,
 * digits and dashes, starting with a letter, at most 20 characters (so
 * "<app>-stage" fits Google's 30-character account-name limit).
 */
export function stackAppName(repoName) {
    let slug = repoName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^[^a-z]+/, "").slice(0, 20).replace(/-+$/, "");
    if (slug.length < 2)
        slug = `app${slug}`.slice(0, 20);
    return slug;
}
/** Cloud SQL instance of one environment: "<app>-<environment>-db". */
export function cloudSqlInstanceName(app, environment) {
    return `${app}-${environment}-db`;
}
/**
 * Secret Manager secret holding an environment's DATABASE_URL. A Cloud SQL
 * database's secret is Terraform's own, so it never collides with one the
 * deploy keeps for a pasted connection string.
 */
export function databaseSecretName(app, environment, database) {
    return `${app}-${environment}-${database === "cloudsql" ? "cloudsql-url" : "database-url"}`;
}
/** Cloud SQL Auth Proxy release the migrate job uses to reach a Cloud SQL database. */
export const CLOUD_SQL_PROXY_VERSION = "v2.14.1";
const APP = /^[a-z][a-z0-9-]{1,19}$/;
const BRANCH = /^[A-Za-z0-9._/-]{1,200}$/;
/**
 * The Cloud Run scaling each environment of a tier gets — the single source
 * for the Terraform tfvars and for the cost estimate.
 */
export function stackEnvSettings(tier) {
    return stackEnvironments(tier).map((environment) => environment === "prod"
        ? { environment, minInstances: 0, maxInstances: 10 }
        : { environment, minInstances: 0, maxInstances: 2 });
}
export function buildStackFiles(input) {
    const { tier, app, defaultBranch, cloud } = input;
    if (cloud.provider === "aws")
        return buildAwsStackFiles(input);
    if (cloud.provider === "azure")
        throw new Error("Azure stacks aren't supported yet.");
    const cloudSql = input.database === "cloudsql";
    if (input.database != null && !cloudSql)
        throw new Error("Not a supported database.");
    const secretSuffix = cloudSql ? "cloudsql-url" : "database-url";
    if (!APP.test(app))
        throw new Error("Not a valid app name.");
    if (!BRANCH.test(defaultBranch))
        throw new Error("Not a valid branch name.");
    const envs = stackEnvironments(tier);
    const registry = `${cloud.region}-docker.pkg.dev/${cloud.projectId}/${app}`;
    const files = [];
    const stateBucket = gcpStateBucket(cloud.projectId);
    // Sign-in and Terraform setup, shared by the deploy and teardown workflows.
    const cloudSetupSteps = `      - uses: google-github-actions/auth@v2
        with:
          workload_identity_provider: ${cloud.workloadProvider}
          service_account: ${cloud.serviceAccountEmail}

      - uses: google-github-actions/setup-gcloud@v2

      - uses: hashicorp/setup-terraform@v3`;
    const terraformInit = `terraform init -input=false -backend-config="bucket=$STATE_BUCKET" -backend-config="prefix=$APP/$ENVIRONMENT"`;
    files.push({
        path: "infra/versions.tf",
        content: `# Managed by CompBox. Changes here are applied on the next deploy.
terraform {
  required_version = ">= 1.6"
  required_providers {
    google = {
      source  = "hashicorp/google"
      version = "~> 6.0"
    }${cloudSql
            ? `
    random = {
      source  = "hashicorp/random"
      version = "~> 3.6"
    }`
            : ""}
  }
  # Bucket and prefix are passed by the deploy workflow.
  backend "gcs" {}
}

provider "google" {
  project = var.project_id
  region  = var.region
}
`,
    });
    files.push({
        path: "infra/variables.tf",
        content: `variable "project_id" {
  type = string
}

variable "region" {
  type = string
}

variable "app" {
  type = string
}

variable "environment" {
  type = string
}

variable "min_instances" {
  type    = number
  default = 0
}

variable "max_instances" {
  type    = number
  default = 2
}

# Only used the first time; every deploy sets the real image.
variable "bootstrap_image" {
  type    = string
  default = "us-docker.pkg.dev/cloudrun/container/hello"
}
`,
    });
    files.push({
        path: "infra/main.tf",
        content: `locals {
  service = "\${var.app}-\${var.environment}"
}

# One image registry for the app, created with dev and shared by every environment.
resource "google_artifact_registry_repository" "app" {
  count         = var.environment == "dev" ? 1 : 0
  repository_id = var.app
  location      = var.region
  format        = "DOCKER"
}

# The account the app runs as in this environment (no permissions by default).
resource "google_service_account" "runtime" {
  account_id   = local.service
  display_name = "\${local.service} (CompBox)"
}

resource "google_cloud_run_v2_service" "app" {
  name                = local.service
  location            = var.region
  ingress             = "INGRESS_TRAFFIC_ALL"
  deletion_protection = var.environment == "prod"

  template {
    service_account = google_service_account.runtime.email
    scaling {
      min_instance_count = var.min_instances
      max_instance_count = var.max_instances
    }
    containers {
      image = var.bootstrap_image
    }
  }

${cloudSql
            ? `  # Deploys set the image, DATABASE_URL and the Cloud SQL connection; Terraform owns everything else.
  lifecycle {
    ignore_changes = [
      template[0].containers[0].image,
      template[0].containers[0].env,
      template[0].containers[0].volume_mounts,
      template[0].volumes,
      client,
      client_version,
    ]
  }`
            : `  # Deploys change the image; Terraform owns everything else.
  lifecycle {
    ignore_changes = [template[0].containers[0].image, client, client_version]
  }`}
}

# Anyone with the link can open it. Remove this to keep the app private.
resource "google_cloud_run_v2_service_iam_member" "public" {
  name     = google_cloud_run_v2_service.app.name
  location = var.region
  role     = "roles/run.invoker"
  member   = "allUsers"
}

output "url" {
  value = google_cloud_run_v2_service.app.uri
}
`,
    });
    if (cloudSql) {
        files.push({
            path: "infra/database.tf",
            content: `# Managed by CompBox: a Cloud SQL for PostgreSQL database for this environment.
# Estimated cost: a db-f1-micro instance with 10 GB of SSD, per environment.
# The password is generated here and kept only in Terraform state and Secret Manager.
resource "google_sql_database_instance" "db" {
  name                = "\${local.service}-db"
  database_version    = "POSTGRES_16"
  region              = var.region
  # Terraform refuses to delete it; turn this off first if you really mean to.
  deletion_protection = true

  settings {
    edition                     = "ENTERPRISE"
    tier                        = "db-f1-micro"
    availability_type           = "ZONAL"
    disk_type                   = "PD_SSD"
    disk_size                   = 10
    deletion_protection_enabled = true

    backup_configuration {
      enabled = true
    }

    # A public IP with no authorized networks: only IAM-authorized connectors
    # (Cloud Run's Cloud SQL connection, the Cloud SQL Auth Proxy) can reach it.
    ip_configuration {
      ipv4_enabled = true
    }
  }
}

resource "google_sql_database" "app" {
  name     = var.app
  instance = google_sql_database_instance.db.name
}

resource "random_password" "db" {
  length  = 32
  special = false
}

resource "google_sql_user" "app" {
  name     = var.app
  instance = google_sql_database_instance.db.name
  password = random_password.db.result
}

# Cloud Run (and the migrate job's proxy) reach the database over a Unix socket in /cloudsql.
resource "google_secret_manager_secret" "database_url" {
  secret_id = "\${local.service}-${secretSuffix}"
  replication {
    auto {}
  }
}

resource "google_secret_manager_secret_version" "database_url" {
  secret      = google_secret_manager_secret.database_url.id
  secret_data = "postgresql://\${google_sql_user.app.name}:\${random_password.db.result}@localhost/\${google_sql_database.app.name}?host=/cloudsql/\${google_sql_database_instance.db.connection_name}"
}

resource "google_secret_manager_secret_iam_member" "runtime" {
  secret_id = google_secret_manager_secret.database_url.id
  role      = "roles/secretmanager.secretAccessor"
  member    = "serviceAccount:\${google_service_account.runtime.email}"
}

resource "google_project_iam_member" "runtime_cloudsql" {
  project = var.project_id
  role    = "roles/cloudsql.client"
  member  = "serviceAccount:\${google_service_account.runtime.email}"
}

output "database_instance" {
  value = google_sql_database_instance.db.connection_name
}
`,
        });
    }
    for (const s of stackEnvSettings(tier)) {
        const env = s.environment;
        files.push({
            path: `infra/envs/${env}.tfvars`,
            content: `project_id    = "${cloud.projectId}"
region        = "${cloud.region}"
app           = "${app}"
environment   = "${env}"
min_instances = ${String(s.minInstances)}
max_instances = ${String(s.maxInstances)}
`,
        });
    }
    const options = envs.map((e) => `          - ${e}`).join("\n");
    files.push({
        path: ".github/workflows/compbox.yml",
        content: `# Managed by CompBox: every push to ${defaultBranch} deploys to dev.${envs.length > 1 ? "\n# Stage and prod: Actions → CompBox → Run workflow. Prod waits for its environment's approval." : ""}
name: CompBox
run-name: Deploy to \${{ inputs.environment || 'dev' }}

on:
  push:
    branches: [${JSON.stringify(defaultBranch)}]
  workflow_dispatch:
    inputs:
      environment:
        description: Where to deploy
        type: choice
        default: dev
        options:
${options}

permissions:
  contents: read
  id-token: write

jobs:
  deploy:
    uses: ./.github/workflows/compbox-deploy.yml
    with:
      environment: \${{ inputs.environment || 'dev' }}
    secrets: inherit
`,
    });
    files.push({
        path: ".github/workflows/compbox-deploy.yml",
        content: `# Managed by CompBox. Signs in to Google Cloud without stored keys (Workload Identity).
#
# 1. build   — runs in the environment (prod waits for its approval): applies the
#              Terraform, ${cloudSql ? "including the Cloud SQL database and its DATABASE_URL" : "keeps DATABASE_URL in Secret Manager"}, builds the image.
# 2. migrate — only when the environment has a DATABASE_URL: runs the app's own
#              "db:migrate" (or "migrate") script. It runs in the "<environment>-migrations"
#              GitHub environment, so you can require an approval before migrations.
# 3. release — deploys the image to Cloud Run once migrations have succeeded.
name: CompBox deploy

on:
  workflow_call:
    inputs:
      environment:
        type: string
        required: true

permissions:
  contents: read
  id-token: write

concurrency:
  group: compbox-\${{ inputs.environment }}
  cancel-in-progress: false

env:
  ENVIRONMENT: \${{ inputs.environment }}
  PROJECT_ID: ${cloud.projectId}
  REGION: ${cloud.region}
  APP: ${app}
  IMAGE: ${registry}/app:\${{ github.sha }}
  STATE_BUCKET: ${stateBucket}

jobs:
  build:
    runs-on: ubuntu-latest
    environment: \${{ inputs.environment }}
    outputs:
      database: \${{ steps.database.outputs.configured }}
    env:
      HAS_DATABASE_URL: \${{ secrets.DATABASE_URL != '' }}
    steps:
      - uses: actions/checkout@v4

${cloudSetupSteps}

      - name: Infrastructure
        working-directory: infra
        run: |
          ${terraformInit}
          terraform apply -input=false -auto-approve -var-file="envs/$ENVIRONMENT.tfvars"

${cloudSql
            ? `      - name: Database secret
        id: database
        # Terraform created the Cloud SQL database and keeps its DATABASE_URL.
        run: echo "configured=true" >> "$GITHUB_OUTPUT"
`
            : `      - name: Database secret
        id: database
        env:
          DATABASE_URL: \${{ secrets.DATABASE_URL }}
        run: |
          if [ "$HAS_DATABASE_URL" != "true" ]; then
            echo "configured=false" >> "$GITHUB_OUTPUT"
            exit 0
          fi
          SECRET="$APP-$ENVIRONMENT-database-url"
          RUNTIME="serviceAccount:$APP-$ENVIRONMENT@$PROJECT_ID.iam.gserviceaccount.com"
          gcloud secrets describe "$SECRET" --project "$PROJECT_ID" >/dev/null 2>&1 || \\
            gcloud secrets create "$SECRET" --project "$PROJECT_ID" --replication-policy=automatic --quiet
          CURRENT=$(gcloud secrets versions access latest --secret "$SECRET" --project "$PROJECT_ID" 2>/dev/null || true)
          if [ "$CURRENT" != "$DATABASE_URL" ]; then
            printf '%s' "$DATABASE_URL" | gcloud secrets versions add "$SECRET" --project "$PROJECT_ID" --data-file=- --quiet
          fi
          gcloud secrets add-iam-policy-binding "$SECRET" --project "$PROJECT_ID" --member="$RUNTIME" \\
            --role=roles/secretmanager.secretAccessor --condition=None --quiet >/dev/null
          echo "configured=true" >> "$GITHUB_OUTPUT"
`}
      - name: Build
        run: |
          if [ ! -f Dockerfile ]; then
            echo "::error::Add a Dockerfile at the repository root: CompBox deploys the app as a container that listens on \\$PORT."
            exit 1
          fi
          gcloud auth configure-docker "$REGION-docker.pkg.dev" --quiet
          docker build -t "$IMAGE" .
          docker push "$IMAGE"

  migrate:
    needs: build
    if: needs.build.outputs.database == 'true'
    runs-on: ubuntu-latest
    environment: \${{ inputs.environment }}-migrations
    steps:
      - uses: actions/checkout@v4

      - uses: google-github-actions/auth@v2
        with:
          workload_identity_provider: ${cloud.workloadProvider}
          service_account: ${cloud.serviceAccountEmail}

      - uses: google-github-actions/setup-gcloud@v2

      - uses: actions/setup-node@v4
        with:
          node-version: lts/*

      - name: Migrate
        run: |
          if [ ! -f package.json ]; then
            echo "::notice::No package.json, so there is no migrate script to run. Skipping migrations."
            exit 0
          fi
          SCRIPT=$(node -e 'const s = require("./package.json").scripts || {}; process.stdout.write(s["db:migrate"] ? "db:migrate" : s.migrate ? "migrate" : "")')
          if [ -z "$SCRIPT" ]; then
            echo "::notice::package.json has no \\"db:migrate\\" or \\"migrate\\" script. Skipping migrations."
            exit 0
          fi
          DATABASE_URL=$(gcloud secrets versions access latest --secret "$APP-$ENVIRONMENT-${secretSuffix}" --project "$PROJECT_ID")
          echo "::add-mask::$DATABASE_URL"
          export DATABASE_URL${cloudSql
            ? `
          # Reach the Cloud SQL database through the Cloud SQL Auth Proxy, on the same /cloudsql socket Cloud Run uses.
          curl -fsSL -o cloud-sql-proxy "https://storage.googleapis.com/cloud-sql-connectors/cloud-sql-proxy/${CLOUD_SQL_PROXY_VERSION}/cloud-sql-proxy.linux.amd64"
          chmod +x cloud-sql-proxy
          sudo mkdir -p /cloudsql && sudo chown "$(id -u)" /cloudsql
          INSTANCE="$PROJECT_ID:$REGION:$APP-$ENVIRONMENT-db"
          ./cloud-sql-proxy --unix-socket /cloudsql "$INSTANCE" &
          for _ in $(seq 1 30); do
            [ -S "/cloudsql/$INSTANCE/.s.PGSQL.5432" ] && break
            sleep 1
          done
          if [ ! -S "/cloudsql/$INSTANCE/.s.PGSQL.5432" ]; then
            echo "::error::Couldn't reach the Cloud SQL database $INSTANCE."
            exit 1
          fi`
            : ""}
          if [ -f pnpm-lock.yaml ]; then
            corepack enable
            pnpm install --frozen-lockfile
            pnpm run "$SCRIPT"
          elif [ -f yarn.lock ]; then
            corepack enable
            yarn install --immutable || yarn install --frozen-lockfile
            yarn run "$SCRIPT"
          else
            if [ -f package-lock.json ]; then npm ci; else npm install; fi
            npm run "$SCRIPT"
          fi

  release:
    needs: [build, migrate]
    if: \${{ !cancelled() && needs.build.result == 'success' && (needs.migrate.result == 'success' || needs.migrate.result == 'skipped') }}
    runs-on: ubuntu-latest
    steps:
      - uses: google-github-actions/auth@v2
        with:
          workload_identity_provider: ${cloud.workloadProvider}
          service_account: ${cloud.serviceAccountEmail}

      - uses: google-github-actions/setup-gcloud@v2

      - name: Deploy
        id: deploy
        env:
          DATABASE: \${{ needs.build.outputs.database }}
        run: |
          SERVICE="$APP-$ENVIRONMENT"
          SECRETS=()
          if [ "$DATABASE" = "true" ]; then
            SECRETS=(--set-secrets "DATABASE_URL=$APP-$ENVIRONMENT-${secretSuffix}:latest"${cloudSql ? ` --add-cloudsql-instances "$PROJECT_ID:$REGION:$APP-$ENVIRONMENT-db"` : ""})
          fi
          gcloud run deploy "$SERVICE" --image "$IMAGE" --region "$REGION" --project "$PROJECT_ID" "\${SECRETS[@]}" --quiet
          URL=$(gcloud run services describe "$SERVICE" --region "$REGION" --project "$PROJECT_ID" --format='value(status.url)')
          echo "url=$URL" >> "$GITHUB_OUTPUT"
          echo "### Live at $URL" >> "$GITHUB_STEP_SUMMARY"
`,
    });
    if (tier === "full") {
        files.push({
            path: `.github/workflows/${TEARDOWN_WORKFLOW}`,
            content: `# Managed by CompBox. Destroys stage or prod; dev is never torn down here.
# Start it from CompBox, or Actions → CompBox teardown → Run workflow and type "${app}-<environment>".
name: CompBox teardown
run-name: Tear down \${{ inputs.environment }}

on:
  workflow_dispatch:
    inputs:
      environment:
        description: What to tear down
        type: choice
        options:
${TEARDOWN_ENVIRONMENTS.map((e) => `          - ${e}`).join("\n")}
      confirm:
        description: Type ${app}-<environment> to confirm
        type: string
        required: true

permissions:
  contents: read
  id-token: write

concurrency:
  group: compbox-\${{ inputs.environment }}
  cancel-in-progress: false

jobs:
  teardown:
    runs-on: ubuntu-latest
    environment:
      name: \${{ inputs.environment }}
    env:
      ENVIRONMENT: \${{ inputs.environment }}
      CONFIRM: \${{ inputs.confirm }}
      APP: ${app}
      STATE_BUCKET: ${stateBucket}
    steps:
      - name: Confirm
        run: |
          case "$ENVIRONMENT" in
            stage|prod) ;;
            *) echo "::error::Only stage and prod can be torn down."; exit 1 ;;
          esac
          if [ "$CONFIRM" != "$APP-$ENVIRONMENT" ]; then
            echo "::error::Type $APP-$ENVIRONMENT to confirm the teardown."
            exit 1
          fi

      - uses: actions/checkout@v4

${cloudSetupSteps}

      - name: Init
        working-directory: infra
        run: ${terraformInit}

      # main.tf keeps prod's service protected; a Terraform override file
      # (created only on the runner, never committed) lifts that for this run.
      - name: Turn off deletion protection
        if: inputs.environment == 'prod'
        working-directory: infra
        run: |
          printf '%s\\n' 'resource "google_cloud_run_v2_service" "app" {' '  deletion_protection = false' '}' > compbox_teardown_override.tf
          terraform apply -input=false -auto-approve -var-file="envs/$ENVIRONMENT.tfvars" -target=google_cloud_run_v2_service.app

      - name: Destroy
        working-directory: infra
        run: terraform destroy -input=false -auto-approve -var-file="envs/$ENVIRONMENT.tfvars"

      - name: Remove state
        run: |
          gcloud storage rm -r "gs://$STATE_BUCKET/$APP/$ENVIRONMENT"
          echo "### $APP-$ENVIRONMENT torn down" >> "$GITHUB_STEP_SUMMARY"
`,
        });
    }
    files.push({
        path: "infra/README.md",
        content: `# Infrastructure (managed by CompBox)

This project deploys to Google Cloud project \`${cloud.projectId}\` (${cloud.region}) as a Cloud Run service per environment: ${envs
            .map((e) => `\`${app}-${e}\``)
            .join(", ")}.

- **Dev:** every push to \`${defaultBranch}\` deploys it.${envs.length > 1
            ? "\n- **Stage and prod:** in GitHub, Actions → CompBox → Run workflow, then pick the environment. Add required reviewers to the `prod` environment (Settings → Environments) so prod waits for approval."
            : ""}
- **The app:** a \`Dockerfile\` at the repository root, for a container that listens on \`$PORT\` (8080).
- **Database:** ${cloudSql
            ? `each environment has a Cloud SQL for PostgreSQL database (\`${app}-<environment>-db\`, in \`database.tf\`), created by the first deploy. Terraform keeps its \`DATABASE_URL\` in Secret Manager (\`${app}-<environment>-cloudsql-url\`) and the deploy gives it to the service as \`DATABASE_URL\`. Deletion protection is on: Terraform won't delete the database. The database password is in the Terraform state (the \`${app}\` prefix of the state bucket), so keep that bucket private.`
            : `when the environment has a \`DATABASE_URL\` secret, the deploy keeps it in Secret Manager (\`${app}-<environment>-database-url\`) and gives it to the service as \`DATABASE_URL\`.`} Before the new version goes live, it runs the app's \`db:migrate\` script (or \`migrate\`) with npm, pnpm or yarn, matching the lockfile; with neither script, migrations are skipped. Migrations run in the \`<environment>-migrations\` GitHub environment: add required reviewers there to approve them before they run. CompBox never runs a reset or a data-loss migration.
- **Sign-in:** GitHub Actions sign in to Google Cloud through Workload Identity — there are no keys to store or rotate.
- **Changes:** edit the Terraform here; the next deploy applies it. Services are public by default; remove the \`public\` resource in \`main.tf\` to keep one private.
`,
    });
    return files;
}
//# sourceMappingURL=stack-files.js.map